import React, { useEffect, useState, useRef, memo, createElement } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, ShieldCheck, Zap, Target, CheckCircle2, Star, Users, Gift, Sparkles, Flower } from 'lucide-react';
import { useMotionValue, useTransform, animate, useInView, AnimatePresence } from 'framer-motion';
import { UrgencyBanner } from './components/UrgencyBanner';
import { BonusCard } from './components/BonusCard';
import { Reviews } from './components/Reviews';
import { FAQ } from './components/FAQ';
import { WhatYouReceive } from './components/WhatYouReceive';
import { ReikiBackground } from './components/ReikiBackground';
import { HeroReikiIcons } from './components/HeroReikiIcons';
import { GiftBackground } from './components/GiftBackground';
import { InfraestructuraSistemica } from './components/InfraestructuraSistemica';
import { DiferencialHibrido } from './components/DiferencialHibrido';
function AnimatedCounter({
  from,
  to
}: {
  from: number;
  to: number;
}) {
  const count = useMotionValue(from);
  const rounded = useTransform(count, latest => {
    return Math.round(latest).toLocaleString('es-AR');
  });
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true
  });
  useEffect(() => {
    if (isInView) {
      const controls = animate(count, to, {
        duration: 1.5,
        ease: 'easeOut'
      });
      return controls.stop;
    }
  }, [count, to, isInView]);
  return <motion.span ref={ref} data-id="element-0">{rounded}</motion.span>;
}
const testimonials = [{
  name: 'Noemí (52)',
  loc: 'Buenos Aires',
  badge: 'Consteladora Familiar',
  image: 'https://images.unsplash.com/photo-1566616213894-2d4e1baee5d8?auto=format&fit=crop&q=80&w=400&h=400',
  text: 'Al principio dudé por el precio, pero el mapa de implicancias sistémicas es oro puro. Me dio la estructura que me faltaba para mis sesiones individuales.'
}, {
  name: 'Valeria (28)',
  loc: 'Córdoba Capital',
  badge: 'Terapeuta Sistémica',
  image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&q=80&w=400&h=400',
  text: 'El kit de encantamiento cambió totalmente la primera impresión de mis consultantes. Las tarjetas y el guion de bienvenida generan un clima de confianza inmediato.'
}, {
  name: 'Mariela (55)',
  loc: 'Rosario',
  badge: 'Facilitadora en Constelaciones',
  image: 'https://images.unsplash.com/photo-1551836022-d5d88e9218df?auto=format&fit=crop&q=80&w=400&h=400',
  text: 'Las visualizaciones para tomar a los padres son las más potentes que he usado. Mis alumnos de la formación están fascinados con la claridad del material.'
}, {
  name: 'Sol (25)',
  loc: 'Mendoza',
  badge: 'Psicóloga Sistémica',
  image: 'https://images.unsplash.com/photo-1524504388940-b1c1722653e1?auto=format&fit=crop&q=80&w=400&h=400',
  text: 'El diario de integración es la herramienta perfecta para que el movimiento sistémico siga trabajando entre sesiones. Mis pacientes se sienten muy acompañados.'
}, {
  name: 'Beatriz (31)',
  loc: 'Salta',
  badge: 'Consteladora Familiar',
  image: 'https://images.unsplash.com/photo-1489424731084-a5d8b219a5bb?auto=format&fit=crop&q=80&w=400&h=400',
  text: 'El protocolo de protección energética me salvó. Antes terminaba agotada después de cada constelación, ahora sé cómo sostener mi lugar en el sistema.'
}];
export function App() {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [spots, setSpots] = useState(18);
  const [joinedCount, setJoinedCount] = useState(3265);
  const [viewers, setViewers] = useState(48);
  const [showSaleAnimations, setShowSaleAnimations] = useState(false);
  useEffect(() => {
    const viewerInterval = setInterval(() => {
      setViewers(prev => prev + (Math.random() > 0.5 ? 1 : -1));
    }, 3000);
    return () => clearInterval(viewerInterval);
  }, []);
  useEffect(() => {
    // Meta Pixel Integration
    const pixelId = '1051253568074079';
    // @ts-ignore
    if (!window.fbq) {
      // @ts-ignore
      !function (f, b, e, v, n, t, s) {
        if (f.fbq) return;
        n = f.fbq = function () {
          n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
        };
        if (!f._fbq) f._fbq = n;
        n.push = n;
        n.loaded = !0;
        n.version = '2.0';
        n.queue = [];
        t = b.createElement(e);
        t.async = !0;
        t.src = v;
        b.head.appendChild(t);
      }(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
    }
    // @ts-ignore
    window.fbq('init', pixelId);
    // @ts-ignore
    window.fbq('track', 'PageView');
  }, []);
  const handleSale = () => {
    setSpots(prev => prev > 2 ? prev - 1 : prev);
    setJoinedCount(prev => prev + 1);
    setShowSaleAnimations(true);
    setTimeout(() => setShowSaleAnimations(false), 2000);
  };
  const nextTestimonial = () => {
    setCurrentTestimonial(prev => (prev + 1) % testimonials.length);
  };
  const prevTestimonial = () => {
    setCurrentTestimonial(prev => (prev - 1 + testimonials.length) % testimonials.length);
  };
  const currentDate = new Date().toLocaleDateString('es-AR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
  const scrollToPurchase = () => {
    document.getElementById('purchase')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <div className="min-h-screen bg-blue-50 text-blue-950 font-sans selection:bg-blue-200 selection:text-blue-950" data-id="element-1">
      <ReikiBackground data-id="element-2" />
      <UrgencyBanner spots={spots} onSale={handleSale} viewers={viewers} data-id="element-3" />

      {/* Hero Section */}
      <header className="relative pt-6 pb-16 overflow-hidden bg-gradient-to-b from-[#0f172a] via-[#1e293b] to-[#334155] text-white" data-id="element-4">
        <HeroReikiIcons data-id="element-5" />
        {/* Magical Background Glows */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full bg-[radial-gradient(ellipse_at_top,_rgba(255,255,255,0.1)_0%,_transparent_60%)] pointer-events-none" data-id="element-6" />
        <div className="absolute top-1/4 left-0 w-64 h-64 bg-blue-500/10 rounded-full blur-[100px] pointer-events-none" data-id="element-7" />
        <div className="absolute bottom-1/4 right-0 w-64 h-64 bg-sky-400/10 rounded-full blur-[100px] pointer-events-none" data-id="element-8" />

        <div className="max-container px-4 relative z-10" data-id="element-9">
          {/* Top Trust Bar */}
          <div className="flex flex-wrap justify-center items-center text-xs md:text-sm font-medium mb-8 text-white/90 border-b border-white/10 pb-4 gap-[8px]" data-id="element-10">
            <div className="flex items-center gap-1.5" data-id="element-11">
              <Star size={14} className="fill-white" data-id="element-12" />
              <span data-id="element-13">
                +<AnimatedCounter from={0} to={3000} data-id="element-14" /> alumnos
              </span>
            </div>
            <div className="flex items-center gap-1.5" data-id="element-15">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-16">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" data-id="element-17" />
              </svg>
              <span data-id="element-18">Acceso inmediato</span>
            </div>
            <div className="flex items-center gap-1.5" data-id="element-19">
              <ShieldCheck size={14} data-id="element-20" />
              <span data-id="element-21">Garantía 7 días</span>
            </div>
          </div>

          <div className="max-w-3xl mx-auto text-center" data-id="element-22">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8
          }} data-id="element-23">
              <div className="mb-6 space-y-2" data-id="element-24">
                <span className="block text-blue-200 font-bold tracking-[0.2em] uppercase md:text-sm text-[16px]" data-id="element-25">
                  Constelación Familiar Terapéutica
                </span>
                <h1 className="md:text-5xl lg:text-6xl font-extrabold tracking-tight text-white text-[32px]" data-id="element-26">
                  ORDENÁ TU SISTEMA Y SANÁ TU HISTORIA
                </h1>
                <div className="pt-2" data-id="element-27">
                  <span className="text-sky-400 font-bold md:text-base tracking-[0.2em] uppercase text-[18px]" data-id="element-28">
                    Sistema Profesional
                  </span>
                </div>
              </div>

              <p className="md:text-xl mb-8 font-medium text-white/80 max-w-2xl mx-auto text-[16px]" data-id="element-29">
                Accedé al kit completo de herramientas sistémicas y modelos
                listos para aplicar con total confianza y seguridad.
              </p>
            </motion.div>

            <div className="relative w-full max-w-lg mx-auto mb-10 h-[300px] md:h-[450px] flex items-center justify-center overflow-hidden" data-id="element-30">
              <motion.img src="https://cdn.magicpatterns.com/uploads/awdiCwerGonwMSrJG7ivKw/hf_20260419_204815_73db72b0-19ff-432e-b50a-3e1355cbe279.webp" alt="Constelación Familiar Terapéutica" initial={{
              opacity: 0,
              scale: 0.9
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              duration: 0.8,
              ease: 'easeOut'
            }} className="w-full h-auto drop-shadow-[0_20px_50px_rgba(0,0,0,0.5)]" data-id="element-31" />
            </div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            delay: 0.4
          }} className="flex flex-col gap-4 max-w-sm mx-auto" data-id="element-32">
              <button onClick={scrollToPurchase} className="w-full bg-gradient-to-b from-[#3b82f6] to-[#1e40af] text-white font-bold py-4 px-6 rounded-full shadow-[0_8px_20px_rgba(30,64,175,0.5)] border border-[#60a5fa] hover:scale-[1.02] transition-transform text-[14px]" data-id="element-33">
                ACCEDER AL SISTEMA PROFESIONAL
              </button>

              <p className="text-white/80 mt-4 text-[12px]" data-id="element-34">
                Unite a miles de personas que ya pusieron orden en su historia
                familiar con este Sistema.
              </p>
            </motion.div>
          </div>
        </div>
      </header>

      {/* Sales Copy */}
      <section className="py-20 md:py-32 bg-white border-y border-blue-100 pt-[30px] pb-[30px]" data-id="element-35">
        <div className="max-w-4xl mx-auto px-4" data-id="element-36">
          <div className="mb-16 md:mb-24" data-id="element-37">
            <div className="flex flex-col items-center mb-12" data-id="element-38">
              <motion.div initial={{
              opacity: 0,
              scale: 0.8
            }} whileInView={{
              opacity: 1,
              scale: 1
            }} viewport={{
              once: true
            }} className="mb-4 text-pink-400/60" data-id="element-39"></motion.div>
              <h2 className="text-2xl md:text-4xl uppercase mb-0 text-center leading-tight font-[Montserrat,_sans-serif] max-w-3xl mx-auto font-black tracking-tight" data-id="element-40">
                <span className="text-pink-700 block md:inline" data-id="element-41">
                  El amor no es suficiente
                </span>{' '}
                <span className="text-blue-950 block md:inline" data-id="element-42">
                  para sanar un sistema familiar.
                </span>
              </h2>
              <div className="w-24 h-1 bg-gradient-to-r from-transparent via-blue-200 to-transparent mt-6" data-id="element-43" />
            </div>

            <div className="grid md:grid-cols-2 gap-6 md:gap-10 mb-12" data-id="element-44">
              <div className="p-6 md:p-8 rounded-[2rem] bg-blue-50/40 border border-blue-100 relative overflow-hidden group transition-all hover:shadow-lg hover:shadow-blue-200/20" data-id="element-45">
                <div className="absolute -top-4 -right-4 opacity-5 group-hover:opacity-10 transition-opacity" data-id="element-46">
                  <Zap size={120} className="text-blue-900" data-id="element-47" />
                </div>
                <div className="flex items-center gap-3 mb-4" data-id="element-48">
                  <div className="w-10 h-10 rounded-full bg-blue-200 flex items-center justify-center text-blue-700" data-id="element-49">
                    <Target size={20} data-id="element-50" />
                  </div>
                  <h4 className="font-black text-blue-900 uppercase tracking-wider text-sm" data-id="element-51">
                    EL PELIGRO DE LA IMPROVISACIÓN
                  </h4>
                </div>
                <p className="text-blue-800/80 leading-relaxed" data-id="element-52">
                  Mover hilos familiares sin una técnica clara es como entrar a
                  un laberinto a ciegas. Si no sabés identificar los órdenes del
                  amor o las lealtades invisibles, podés generar más confusión
                  de la que intentás resolver. La buena voluntad no reemplaza al
                  método.
                </p>
              </div>

              <div className="p-6 md:p-8 rounded-[2rem] bg-pink-50/40 border border-pink-100 relative overflow-hidden group transition-all hover:shadow-lg hover:shadow-pink-200/20" data-id="element-53">
                <div className="absolute -top-4 -right-4 opacity-5 group-hover:opacity-10 transition-opacity" data-id="element-54">
                  <Users size={120} className="text-pink-900" data-id="element-55" />
                </div>
                <div className="flex items-center gap-3 mb-4" data-id="element-56">
                  <div className="w-10 h-10 rounded-full bg-pink-200 flex items-center justify-center text-pink-700" data-id="element-57">
                    <Star size={20} data-id="element-58" />
                  </div>
                  <h4 className="font-black text-pink-900 uppercase tracking-wider text-sm" data-id="element-59">
                    EL AGOTAMIENTO POR ENREDO
                  </h4>
                </div>
                <p className="text-pink-800/80 leading-relaxed italic" data-id="element-60">
                  Dejá de adivinar y empezá a intervenir con precisión clínica.
                  Para transformar el dolor en fuerza, necesitás herramientas
                  que funcionen bajo presión. Necesitás el Sistema Profesional.
                </p>
              </div>
            </div>

            <div className="flex flex-col md:flex-row items-center gap-6 p-6 md:p-8 bg-blue-50/30 border border-blue-100 rounded-[2rem] text-blue-950 shadow-sm" data-id="element-61">
              <div className="shrink-0 w-12 h-12 rounded-2xl bg-blue-100 flex items-center justify-center text-blue-600" data-id="element-62">
                <ShieldCheck size={24} data-id="element-63" />
              </div>
              <p className="text-lg md:text-xl font-medium leading-relaxed text-center md:text-left" data-id="element-64">
                Dejá de adivinar y empezá a intervenir con precisión clínica.
                Para transformar el dolor en fuerza, necesitás herramientas que
                funcionen bajo presión. Necesitás el Sistema Profesional.
              </p>
            </div>
          </div>

          <DiferencialHibrido data-id="element-65" />
          <InfraestructuraSistemica data-id="element-66" />
        </div>
      </section>

      <WhatYouReceive data-id="element-67" />

      {/* Bonuses */}
      <section className="py-24 bg-blue-50 pt-[40px] pb-[40px] relative overflow-hidden" data-id="element-68">
        <GiftBackground data-id="element-69" />

        <div className="max-container px-4 relative z-10" data-id="element-70">
          <div className="text-center mb-16" data-id="element-71">
            <motion.div initial={{
            scale: 0.9,
            opacity: 0
          }} whileInView={{
            scale: 1,
            opacity: 1
          }} viewport={{
            once: true
          }} className="inline-flex items-center justify-center mb-6" data-id="element-72">
              <div className="relative" data-id="element-73">
                <motion.div animate={{
                rotate: [0, -10, 10, -10, 0]
              }} transition={{
                duration: 2,
                repeat: Infinity
              }} className="bg-blue-600 text-white p-4 rounded-2xl shadow-xl relative z-10" data-id="element-74">
                  <Gift size={40} data-id="element-75" />
                </motion.div>
                <motion.div animate={{
                scale: [1, 1.2, 1],
                opacity: [0.5, 0.8, 0.5]
              }} transition={{
                duration: 2,
                repeat: Infinity
              }} className="absolute -inset-4 bg-blue-400/30 rounded-full blur-xl" data-id="element-76" />
              </div>
            </motion.div>

            <h2 className="md:text-7xl font-black text-blue-950 uppercase italic mb-4 text-[32px] font-[Poppins,_sans-serif] tracking-tight" data-id="element-77">
              ¡Regalos <span className="text-blue-600" data-id="element-78">Exclusivos!</span>
            </h2>
            <div className="flex items-center justify-center gap-2" data-id="element-79">
              <Sparkles className="text-yellow-500 animate-pulse" size={20} data-id="element-80" />
              <p className="text-blue-600 font-black tracking-[0.2em] uppercase text-sm" data-id="element-81">
                100% GRATIS SOLO POR HOY
              </p>
              <Sparkles className="text-yellow-500 animate-pulse" size={20} data-id="element-82" />
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-8" data-id="element-83">
            <BonusCard number="01" title="Kit de encantamiento para la primera sesión" image="https://cdn.magicpatterns.com/uploads/pmRzXRZqmi8YYai2fYf7pQ/Diseno_sin_titulo_(1).webp" description="Cree una experiencia inolvidable desde el primer contacto con el cliente. Tarjetas, minicuestionario y guion de bienvenida." value="$47 USD" data-id="element-84" />
            <BonusCard number="02" title="Cartas terapéuticas: Ho'oponopono" image="https://cdn.magicpatterns.com/uploads/bzJHKSegp2KUJuhsotu3ia/Diseno_sin_titulo_(3).webp" description="Herramienta para sanar vínculos y liberar memorias de dolor a través del perdón y la gratitud." value="$37 USD" data-id="element-85" />
            <BonusCard number="03" title="Biblioteca de Meditaciones Sistémicas" image="https://cdn.magicpatterns.com/uploads/viHe2PLREipfnY3drA8oSs/Diseno_sin_titulo_(4).webp" description="50 guiones listos de meditación para la integración emocional y el equilibrio del sistema familiar." value="$27 USD" data-id="element-86" />
            <BonusCard number="04" title="Diario de integración y realineación" image="https://cdn.magicpatterns.com/uploads/wi7scaPramRe8ZCZEVAC2k/Diseno_sin_titulo_(8).webp" description="Una herramienta práctica para ayudar a los clientes a mantener un movimiento terapéutico activo entre sesiones." value="$37 USD" data-id="element-87" />
            <BonusCard number="05" title="Protocolo de Protección Energética" image="https://cdn.magicpatterns.com/uploads/pVt6KcawPPmAL15TJPFkkm/Diseno_sin_titulo_(6).webp" description="Técnicas prácticas para limpiar, proteger y sostener la energía del terapeuta antes y después de las sesiones." value="$27 USD" data-id="element-88" />
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-32 relative overflow-hidden bg-gradient-to-b from-blue-50 to-sky-50 pt-[5px] pb-[5px]" data-id="element-89">
        {/* Soft blur background elements */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-blue-100/80 via-transparent to-transparent pointer-events-none" data-id="element-90" />
        <div className="absolute -left-40 top-40 w-96 h-96 bg-blue-200/20 rounded-full blur-3xl pointer-events-none" data-id="element-91" />
        <div className="absolute -right-40 bottom-20 w-96 h-96 bg-blue-100 rounded-full blur-3xl shadow-[0_0_100px_rgba(30,64,175,0.1)] pointer-events-none" data-id="element-92" />

        <div className="max-container px-4 relative z-10" data-id="element-93">
          <div className="text-center mb-16" data-id="element-94">
            <h3 className="md:text-5xl text-blue-950 uppercase tracking-tighter italic font-[800] font-[Poppins,_sans-serif] pt-[20px] pb-[0px] text-[20px]" data-id="element-95">
              Comentarios destacados ⭐
            </h3>
          </div>

          <div className="max-w-4xl mx-auto relative" data-id="element-96">
            <div className="overflow-hidden" data-id="element-97">
              <motion.div key={currentTestimonial} drag="x" dragConstraints={{
              left: 0,
              right: 0
            }} onDragEnd={(_, info) => {
              if (info.offset.x > 100) {
                prevTestimonial();
              } else if (info.offset.x < -100) {
                nextTestimonial();
              }
            }} initial={{
              opacity: 0,
              x: 100
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -100
            }} transition={{
              duration: 0.5,
              ease: 'easeOut'
            }} className="bg-white p-8 md:p-12 rounded-[3rem] shadow-[0_30px_60px_-12px_rgba(30,64,175,0.15)] border border-blue-100 relative backdrop-blur-sm cursor-grab active:cursor-grabbing" data-id="element-98">
                <div className="flex flex-col md:flex-row gap-8 items-center md:items-start text-center md:text-left" data-id="element-99">
                  <div className="shrink-0 relative" data-id="element-100">
                    <div className="absolute -inset-2 bg-gradient-to-br from-blue-400 to-sky-400 rounded-full blur opacity-30 animate-pulse" data-id="element-101" />
                    <img src={testimonials[currentTestimonial].image} alt={testimonials[currentTestimonial].name} className="w-32 h-32 md:w-40 md:h-40 rounded-full object-cover border-4 border-white shadow-xl relative z-10" data-id="element-102" />
                  </div>

                  <div className="flex-1" data-id="element-103">
                    <div className="flex justify-center md:justify-start text-pink-500 mb-6 gap-1" data-id="element-104">
                      {[...Array(5)].map((_, idx) => <Star key={idx} size={20} fill="currentColor" strokeWidth={1} data-id="element-105" />)}
                    </div>

                    <p className="text-blue-950 text-xl md:text-2xl leading-relaxed font-light italic mb-8" data-id="element-106">
                      "{testimonials[currentTestimonial].text}"
                    </p>

                    <div className="pt-6 border-t border-blue-50" data-id="element-107">
                      <span className="inline-block bg-blue-100 text-blue-700 text-xs font-black uppercase tracking-widest px-3 py-1 rounded-full mb-2" data-id="element-108">
                        {testimonials[currentTestimonial].badge}
                      </span>
                      <h4 className="text-blue-950 font-black text-xl" data-id="element-109">
                        {testimonials[currentTestimonial].name}
                      </h4>
                      <p className="text-blue-400 font-bold uppercase tracking-widest text-sm" data-id="element-110">
                        {testimonials[currentTestimonial].loc}
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Navigation Arrows */}
            <button onClick={prevTestimonial} className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 md:-translate-x-12 bg-white p-4 rounded-full shadow-xl text-pink-600 hover:bg-pink-50 transition-colors z-20 hidden md:block" data-id="element-111">
              <ArrowRight className="rotate-180" size={24} data-id="element-112" />
            </button>
            <button onClick={nextTestimonial} className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 md:translate-x-12 bg-white p-4 rounded-full shadow-xl text-pink-600 hover:bg-pink-50 transition-colors z-20 hidden md:block" data-id="element-113">
              <ArrowRight size={24} data-id="element-114" />
            </button>

            {/* Pagination Dots */}
            <div className="flex justify-center gap-3 mt-12" data-id="element-115">
              {testimonials.map((_, i) => <button key={i} onClick={() => setCurrentTestimonial(i)} className={`h-3 rounded-full transition-all duration-300 ${currentTestimonial === i ? 'w-12 bg-pink-500' : 'w-3 bg-pink-200 hover:bg-pink-300'}`} data-id="element-116" />)}
            </div>
          </div>
        </div>
      </section>

      {/* Purchase Section */}
      <section id="purchase" className="py-24 bg-blue-100 relative overflow-hidden pt-[30px] pb-[30px]" data-id="element-117">
        <div className="max-w-4xl mx-auto px-4 relative z-10" data-id="element-118">
          <div className="bg-blue-950 text-white p-4 text-center font-black uppercase tracking-widest mb-8 animate-pulse" data-id="element-119">
            OFERTA VÁLIDA SOLO POR HOY: {currentDate}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-12" data-id="element-120">
            {['Descargue todos los archivos, guárdelos en una carpeta segura y téngalos para siempre.', 'Pago único y descarga inmediata.', 'Materiales listos para impresión.', 'Herramientas desarrolladas por terapeutas especialistas.'].map((text, i) => <div key={i} className="flex items-center gap-3 bg-white/50 backdrop-blur-sm p-4 rounded-2xl border border-blue-200 shadow-sm" data-id="element-121">
                <CheckCircle2 className="text-blue-600 shrink-0" size={20} data-id="element-122" />
                <p className="text-blue-900 text-sm font-bold leading-tight" data-id="element-123">
                  {text}
                </p>
              </div>)}
          </div>

          <div className="mb-12" data-id="element-124">
            <img src="https://cdn.magicpatterns.com/uploads/2H8Jgn9LK4MzDkw8mnnFc3/Diseno_sin_titulo_(2).webp" alt="Sistema Profesional de Constelaciones Familiares" className="w-full max-w-5xl mx-auto h-auto drop-shadow-[0_20px_50px_rgba(0,0,0,0.2)]" data-id="element-125" />
          </div>

          <div className="max-w-4xl mx-auto" data-id="element-126">
            <div className="bg-white border-4 border-blue-950 rounded-[2.5rem] shadow-2xl overflow-hidden" data-id="element-127">
              <div className="p-6 md:p-10" data-id="element-128">
                <div className="grid md:grid-cols-12 gap-8 items-center" data-id="element-129">
                  {/* Left Column: Content */}
                  <div className="md:col-span-7 space-y-6" data-id="element-130">
                    <div className="bg-blue-50 p-6 rounded-2xl border border-blue-100" data-id="element-131">
                      <h4 className="text-blue-950 font-black uppercase tracking-widest text-xs mb-4 flex items-center gap-2" data-id="element-132">
                        <Sparkles size={16} className="text-blue-600" data-id="element-133" /> LO QUE
                        TE LLEVAS HOY:
                      </h4>
                      <ul className="grid grid-cols-1 gap-y-2.5" data-id="element-134">
                        {['Sistema de Constelaciones Familiares (Manual)', 'Bono 1: Kit de encantamiento para la primera sesión', "Bono 2: Cartas terapéuticas: Ho'oponopono", 'Bono 3: Biblioteca de Meditaciones Sistémicas', 'Bono 4: Diario de integración y realineación', 'Bono 5: Protocolo de Protección Energética'].map((item, idx) => <li key={idx} className="flex items-center gap-3 text-blue-900 font-bold text-sm" data-id="element-135">
                            <CheckCircle2 className="text-green-600 shrink-0" size={18} data-id="element-136" />
                            {item}
                          </li>)}
                      </ul>
                    </div>

                    <div className="flex flex-wrap justify-center items-center gap-8 px-2 pt-4" data-id="element-137">
                      <div className="flex items-center gap-8" data-id="element-138">
                        <div className="flex flex-col items-center" data-id="element-139">
                          <span className="text-green-600 flex items-center gap-1.5 font-black text-xl" data-id="element-140">
                            <Users size={20} data-id="element-141" /> {viewers}
                          </span>
                          <span className="text-blue-400 text-[10px] uppercase font-black tracking-widest" data-id="element-142">
                            Mirando
                          </span>
                        </div>
                        <div className="flex flex-col items-center relative" data-id="element-143">
                          <span className="text-red-600 flex items-center gap-1.5 font-black text-xl" data-id="element-144">
                            <Zap size={20} data-id="element-145" /> {spots}
                          </span>
                          <AnimatePresence data-id="element-146">
                            {showSaleAnimations && <motion.span initial={{
                            opacity: 0,
                            y: 0
                          }} animate={{
                            opacity: 1,
                            y: -15
                          }} exit={{
                            opacity: 0
                          }} className="absolute -right-3 top-0 text-red-600 font-bold text-sm" data-id="element-147">
                                -1
                              </motion.span>}
                          </AnimatePresence>
                          <span className="text-blue-400 text-[10px] uppercase font-black tracking-widest" data-id="element-148">
                            Cupos
                          </span>
                        </div>
                      </div>
                      <div className="flex flex-col items-center relative" data-id="element-149">
                        <div className="text-blue-950 font-black text-2xl" data-id="element-150">
                          <AnimatedCounter from={joinedCount - 1} to={joinedCount} data-id="element-151" />
                        </div>
                        <AnimatePresence data-id="element-152">
                          {showSaleAnimations && <motion.span initial={{
                          opacity: 0,
                          y: 0
                        }} animate={{
                          opacity: 1,
                          y: -15
                        }} exit={{
                          opacity: 0
                        }} className="absolute -right-3 top-0 text-green-600 font-bold text-sm" data-id="element-153">
                              +1
                            </motion.span>}
                        </AnimatePresence>
                        <div className="text-blue-400 text-[10px] uppercase font-black tracking-widest" data-id="element-154">
                          Alumnos ya dentro
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Right Column: Pricing & CTA */}
                  <div className="md:col-span-5 flex flex-col items-center text-center space-y-6" data-id="element-155">
                    <div className="space-y-1" data-id="element-156">
                      <p className="text-blue-300 line-through text-xl font-bold" data-id="element-157">
                        Antes $59.900 ARS
                      </p>
                      <p className="text-6xl md:text-7xl font-black text-[#319E0D] tracking-tighter" data-id="element-158">
                        $19.990
                      </p>
                      <p className="text-[#319E0D] font-black text-sm tracking-widest" data-id="element-159">
                        ARS / PAGO ÚNICO
                      </p>
                    </div>

                    <div className="w-full space-y-4" data-id="element-160">
                      <a href="https://codex-labs.shop/cart/43433734733883:1" target="_blank" rel="noopener noreferrer" className="w-full bg-[#319E0D] text-white py-5 rounded-2xl font-black text-2xl flex items-center justify-center gap-3 hover:bg-[#2a860b] transition-all shadow-[0_10px_20px_rgba(49,158,13,0.3)] hover:scale-[1.02] active:scale-95 uppercase tracking-tighter" data-id="element-161">
                        ¡QUIERO MI OFERTA!
                        <ArrowRight size={28} data-id="element-162" />
                      </a>

                      <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 rounded-xl border border-blue-100 w-full justify-center" data-id="element-163">
                        <span className="text-blue-950 font-bold text-sm" data-id="element-164">
                          💳 3 cuotas sin interés
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Trust Section */}
                <div className="mt-10 pt-10 border-t-2 border-dashed border-blue-100 space-y-12" data-id="element-165">
                  {/* Primary Trust Signals - Large and Stacked */}
                  <div className="text-center space-y-[16px]" data-id="element-166">
                    <p className="text-xs font-black text-blue-400 uppercase tracking-[0.2em]" data-id="element-167">
                      Vendedor Destacado
                    </p>
                    <img src="https://cdn.magicpatterns.com/uploads/skccFFLzCHBogMzzjkfEmF/7ef4071d-c511-4325-9d9d-f2158f7b8632.jpg" alt="MercadoLíder Platinum" className="h-28 md:h-32 mx-auto object-contain shadow-md rounded-2xl" data-id="element-168" />
                  </div>

                  <div className="text-center space-y-4" data-id="element-169">
                    <p className="text-xs font-black text-blue-400 uppercase tracking-[0.2em]" data-id="element-170">
                      Pagos Procesados de Forma Segura
                    </p>
                    <img src="https://cdn.magicpatterns.com/uploads/eU118ieSDgWuz64T6oQtAx/capture_260410_163646.png" alt="Pago Seguro Garantizado" className="w-full max-w-[450px] mx-auto object-contain" data-id="element-171" />
                  </div>

                  {/* Secondary Trust Signals - Row */}
                  <div className="border-t border-blue-50 pt-[0px] mt-[0px]" data-id="element-172">
                    <p className="text-blue-950 font-black text-sm uppercase tracking-widest mb-8 text-center" data-id="element-173">
                      Tu Compra está Protegida
                    </p>
                    <div className="flex md:gap-20 gap-[0px] flex-row items-center justify-center" data-id="element-174">
                      <img src="https://cdn.magicpatterns.com/uploads/7eCtKk7WA3cLyGaeie1cdM/png-clipart-safe-purchase-payment-security-shop-shopping-online-store-e-commerce-sign-green-texta.png" alt="Compra Segura" className="h-20 md:h-24 object-contain" data-id="element-175" />
                      <img src="https://cdn.magicpatterns.com/uploads/fpbSfdYZaTGuGJTHBKUizP/Selo_de_Garantia_de_7_Dias_PNG_Transparente_Sem_Fundo.png" alt="Garantía de 7 Días" className="h-24 md:h-32 object-contain" data-id="element-176" />
                    </div>
                  </div>

                  <div className="flex flex-col items-center gap-4 pt-4" data-id="element-177">
                    <p className="text-blue-400 text-xs font-bold flex items-center gap-2 uppercase tracking-widest" data-id="element-178">
                      <ShieldCheck size={16} className="text-green-600" data-id="element-179" /> Pago
                      100% Seguro y Encriptado
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Reviews data-id="element-180" />

      <FAQ data-id="element-181" />

      <footer className="py-12 border-t border-blue-100 text-center text-blue-600 text-sm" data-id="element-182">
        <p data-id="element-183">
          © 2025 Sistema de Constelaciones Familiares. Todos los derechos
          reservados.
        </p>
      </footer>
    </div>;
}